import { motion } from 'framer-motion';
import { FiSmartphone, FiCode, FiLayers, FiDatabase, FiCloud, FiCheck, FiArrowRight, FiUsers, FiTrendingUp, FiAward, FiPlay } from 'react-icons/fi';
import { Helmet } from "react-helmet";
import { useState } from 'react';

const MobileAppsPage = () => {
    const [activePlatform, setActivePlatform] = useState('ios');
    const [expandedFeature, setExpandedFeature] = useState(null);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    const platforms = {
        ios: ['Swift', 'SwiftUI', 'Objective-C', 'Xcode', 'TestFlight'],
        android: ['Kotlin', 'Android SDK', 'Java', 'Android Studio', 'Google Play Console'],
        crossPlatform: ['React Native', 'Flutter', 'Ionic', 'Xamarin', 'Expo']
    };

    const features = [
        {
            title: "Native Performance",
            icon: <FiSmartphone className="text-3xl" />,
            description: "Leverage full device capabilities for buttery-smooth experiences",
            details: "Our apps utilize platform-specific optimizations to deliver performance indistinguishable from built-in apps."
        },
        {
            title: "Custom UI/UX",
            icon: <FiLayers className="text-3xl" />,
            description: "Tailored interfaces that match your brand identity",
            details: "We design pixel-perfect interfaces following platform guidelines while incorporating your unique brand elements."
        },
        {
            title: "Cloud Integration",
            icon: <FiCloud className="text-3xl" />,
            description: "Seamless backend connectivity and data sync",
            details: "Integrated solutions with Firebase, AWS, or custom backends for real-time data and offline capabilities."
        },
        {
            title: "Security First",
            icon: <FiDatabase className="text-3xl" />,
            description: "Enterprise-grade data protection",
            details: "Implementing best practices like encryption, secure storage, and biometric authentication."
        },
        {
            title: "Maintenance Plans",
            icon: <FiCode className="text-3xl" />,
            description: "Ongoing support and updates",
            details: "Post-launch maintenance packages to keep your app current with OS updates and new features."
        },
        {
            title: "App Store Optimization",
            icon: <FiTrendingUp className="text-3xl" />,
            description: "Maximize visibility and downloads",
            details: "We optimize your app listing with strategic keywords, compelling visuals, and conversion-focused descriptions."
        }
    ];

    const processSteps = [
        {
            step: "01",
            title: "Discovery & Strategy",
            description: "Requirement analysis, market research, and planning",
            icon: "🔍"
        },
        {
            step: "02",
            title: "UI/UX Design",
            description: "Wireframing, prototyping, and user experience design",
            icon: "🎨"
        },
        {
            step: "03",
            title: "Development",
            description: "Agile development with continuous testing and iteration",
            icon: "💻"
        },
        {
            step: "04",
            title: "Testing & QA",
            description: "Comprehensive testing across devices and scenarios",
            icon: "🧪"
        },
        {
            step: "05",
            title: "Deployment",
            description: "App store submission and optimization",
            icon: "🚀"
        },
        {
            step: "06",
            title: "Maintenance",
            description: "Ongoing support, updates, and feature additions",
            icon: "🔧"
        }
    ];

    return (
        <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
            <Helmet>
                <title>Mobile App Development Services | iOS & Android Apps</title>
                <meta
                    name="description"
                    content="UniTechnoStack builds high-performing mobile apps for iOS and Android with scalable architecture, sleek UI/UX, and secure integrations to grow your business."
                />
                <meta
                    name="keywords"
                    content="mobile app development services, iOS apps, Android apps, hybrid apps, custom mobile applications, UniTechnoStack"
                />
                <meta name="robots" content="index, follow" />
                <link
                    rel="canonical"
                    href="https://www.unitechnostack.com/mobile-app-development"
                />

                {/* Open Graph tags */}
                <meta
                    property="og:title"
                    content="Mobile App Development Services | iOS & Android Apps"
                />
                <meta
                    property="og:description"
                    content="We design and develop scalable, user-friendly iOS and Android apps to help businesses grow faster."
                />
                <meta
                    property="og:url"
                    content="https://www.unitechnostack.com/mobileapp"
                />
                <meta property="og:type" content="website" />
            </Helmet>

            {/* Hero Section */}
            <section className="relative py-28 px-4 sm:px-6 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white overflow-hidden">
                {/* Animated background elements */}
                <div className="absolute inset-0 overflow-hidden opacity-20">
                    {[...Array(15)].map((_, i) => (
                        <motion.div
                            key={i}
                            animate={{
                                y: [0, -100, 0],
                                x: [0, Math.random() * 80 - 40, 0],
                                rotate: [0, 360],
                                scale: [0.8, 1.2, 0.8]
                            }}
                            transition={{
                                duration: 15 + Math.random() * 15,
                                repeat: Infinity,
                                repeatType: "reverse",
                                ease: "linear"
                            }}
                            className="absolute rounded-lg bg-white/10"
                            style={{
                                width: `${Math.random() * 80 + 40}px`,
                                height: `${Math.random() * 80 + 40}px`,
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                            }}
                        />
                    ))}
                </div>

                {/* Grid pattern overlay */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }} />
                </div>

                <div className="max-w-7xl mx-auto relative z-10 mt-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                            <span className="w-2 h-2 bg-blue-300 rounded-full mr-2 animate-pulse"></span>
                            MOBILE APP DEVELOPMENT
                        </div>
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">
                            Transform Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300">Business</span> With Mobile
                        </h1>
                        <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                            Custom mobile solutions designed to engage users and drive growth
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="flex flex-wrap justify-center gap-4"
                    >
                        <a href="/quickenquiry">
                            <button className="px-6 py-3 bg-white text-blue-900 rounded-full font-medium hover:bg-gray-100 transition-colors shadow-lg flex items-center">
                                Get Free Consultation <FiArrowRight className="ml-2" />
                            </button>
                        </a>
                        <a href="/portfolio">
                            <button className="px-6 py-3 border border-white text-white rounded-full font-medium hover:bg-white/10 transition-colors">
                                View Our Work
                            </button>
                        </a>
                    </motion.div>

                    {/* Stats Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
                    >
                        {[
                            { value: '10+', label: 'Apps Developed' },
                            { value: '4.8/5', label: 'App Store Rating' },
                            { value: '1M+', label: 'Downloads' },
                            { value: '24/7', label: 'Support' }
                        ].map((stat, index) => (
                            <div key={index} className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                                <div className="text-2xl font-bold mb-1">{stat.value}</div>
                                <div className="text-sm text-blue-200">{stat.label}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Platform Selector */}
            <section className="py-20 px-4 sm:px-6 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100"
                >
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold mb-4">Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Platform Expertise</span></h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            We develop for all major platforms using the latest technologies and best practices
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                        {Object.keys(platforms).map((platform) => (
                            <motion.button
                                key={platform}
                                onClick={() => setActivePlatform(platform)}
                                whileHover={{ y: -5 }}
                                className={`p-6 rounded-xl transition-all ${activePlatform === platform
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                    }`}
                            >
                                <div className="font-medium text-lg capitalize mb-2">
                                    {(() => {
                                        switch (platform) {
                                            case 'ios':
                                                return 'iOS Development';
                                            case 'android':
                                                return 'Android Development';
                                            default:
                                                return 'Cross-Platform';
                                        }
                                    })()}
                                </div>
                                <p className="text-sm opacity-80">
                                    {platform === 'ios' && 'Native iOS apps with Swift and SwiftUI'}
                                    {platform === 'android' && 'Native Android apps with Kotlin and Java'}
                                    {platform === 'crossPlatform' && 'Cross-platform solutions with React Native and Flutter'}
                                </p>
                            </motion.button>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {platforms[activePlatform].map((tech, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -5, backgroundColor: "#f8fafc" }}
                                className="bg-white p-4 rounded-lg border border-gray-200 transition-all shadow-sm hover:shadow-md"
                            >
                                <div className="flex items-center">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                        <FiCheck className="text-blue-600" />
                                    </div>
                                    <span className="font-medium">{tech}</span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </section>

            {/* App Showcase */}
            <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-gray-50">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl font-bold mb-4">
                            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">App Development</span> Process
                        </h2>
                        <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                            A streamlined approach to deliver exceptional mobile experiences
                        </p>
                    </motion.div>

                    <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 to-purple-500 block"></div>

                        <div className="grid md:grid-cols-1 gap-6 relative">
                            {processSteps.map((step, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="flex flex-col items-center text-center"
                                >
                                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4 relative">
                                        {step.icon}
                                        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full hidden lg:block"></div>
                                    </div>
                                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 md:w-96">
                                        <div className="text-sm text-blue-600 font-semibold mb-2">STEP {step.step}</div>
                                        <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                                        <p className="text-gray-600">{step.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Features */}
            <section className="py-20 px-4 sm:px-6 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold mb-4">
                        Why Choose Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Mobile Development</span>
                    </h2>
                    <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                        We combine technical excellence with creative design to deliver exceptional results
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className={`bg-white rounded-2xl p-6 transition-all border-l-4 ${expandedFeature === index
                                ? 'border-blue-600 shadow-lg'
                                : 'border-transparent hover:border-blue-200 shadow-md hover:shadow-lg'
                                }`}
                            onClick={() => setExpandedFeature(expandedFeature === index ? null : index)}
                        >
                            <div className="flex items-start">
                                <div className="text-blue-600 bg-blue-50 p-3 rounded-lg mr-4">
                                    {feature.icon}
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                    <p className="text-gray-600 mb-3">{feature.description}</p>
                                    {expandedFeature === index && (
                                        <motion.p
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="text-gray-600 text-sm bg-blue-50 p-3 rounded-lg mt-2"
                                        >
                                            {feature.details}
                                        </motion.p>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>


            {/* CTA Section */}
            <section className="py-20 px-6 max-w-7xl mx-auto">
                <div className="bg-gradient-to-r from-blue-700 to-purple-800 text-white rounded-2xl p-8 sm:p-10 md:p-12 text-center relative overflow-hidden">
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>

                    <div className="relative z-10">
                        <h2 className="text-3xl font-bold mb-4">Ready to Build Your Mobile App?</h2>
                        <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                            Let's discuss how we can create a custom mobile experience for your business
                        </p>
                        <a href="/quickenquiry">
                            <button className="px-8 py-4 bg-white text-blue-900 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl flex items-center mx-auto">
                                Start Your Project
                                <FiArrowRight className="ml-2" />
                            </button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Video Modal */}
            {isVideoModalOpen && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl overflow-hidden w-full max-w-4xl aspect-video">
                        <div className="relative">
                            <button
                                onClick={() => setIsVideoModalOpen(false)}
                                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                            >
                                &times;
                            </button>
                            <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                <p className="text-white">Video content would appear here</p>
                                {/* In a real implementation, you would embed a video player here */}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileAppsPage;

